/** Receivables ageing (§4.9A), detail and summary, with Excel export. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { api, getAccessToken } from '../../lib/api';
import { peso, shortDate } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

type Bucket = 'CURRENT' | 'D31_60' | 'D61_90' | 'D91_120' | 'D120_PLUS';

interface DetailRow {
  billingId: string;
  code: string;
  customerId: string;
  customerName: string;
  salesOrderCode: string;
  billingDate: string;
  dueDate: string;
  total: string;
  settled: string;
  outstanding: string;
  days: number;
  bucket: Bucket;
  overdue: boolean;
}

interface Report {
  asOf: string;
  basis: 'BILLING_DATE' | 'DUE_DATE';
  generatedBy: string;
  generatedAt: string;
  detail: DetailRow[];
  summary: {
    customerId: string;
    customerName: string;
    buckets: Record<Bucket, string>;
    total: string;
  }[];
  bucketTotals: Record<Bucket, string>;
  grandTotal: string;
  bucketLabels: Record<Bucket, string>;
}

interface CustomerOption {
  id: string;
  name: string;
}

const BUCKET_ORDER: Bucket[] = ['CURRENT', 'D31_60', 'D61_90', 'D91_120', 'D120_PLUS'];

export function ArAgingPage(): JSX.Element {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [customerId, setCustomerId] = useState('');
  const [government, setGovernment] = useState('');
  const [view, setView] = useState<'summary' | 'detail'>('summary');

  const params = new URLSearchParams({ asOf });
  if (customerId) params.set('customerId', customerId);
  if (government) params.set('government', government);

  const report = useQuery({
    queryKey: ['ar-aging', params.toString()],
    queryFn: () => api<{ data: Report }>(`/reports/ar-aging?${params.toString()}`),
  });
  const customers = useQuery({
    queryKey: ['customers', 'options'],
    queryFn: () => api<{ data: CustomerOption[] }>('/customers?pageSize=200'),
  });

  const exportExcel = async (): Promise<void> => {
    const response = await fetch(`/api/v1/reports/ar-aging/export?${params.toString()}`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ITCORE-SF_AR-Aging_${asOf}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const data = report.data?.data;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Receivables ageing</h1>
          <p className="text-sm text-slate-500">
            What customers owe, by age.{' '}
            {data && (
              <>
                Counted from the{' '}
                <strong>{data.basis === 'DUE_DATE' ? 'due date' : 'billing date'}</strong>.
              </>
            )}
          </p>
        </div>
        <Button variant="secondary" onClick={() => void exportExcel()}>
          <Download aria-hidden="true" className="h-4 w-4" /> Export Excel
        </Button>
      </header>

      <Card>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="As of">
            <input
              className={inputClass}
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </Field>
          <Field label="Customer">
            <select
              className={inputClass}
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
            >
              <option value="">All customers</option>
              {customers.data?.data.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sector">
            <select
              className={inputClass}
              value={government}
              onChange={(event) => setGovernment(event.target.value)}
            >
              <option value="">All</option>
              <option value="true">Government only</option>
              <option value="false">Private only</option>
            </select>
          </Field>
          <Field label="View">
            <select
              className={inputClass}
              value={view}
              onChange={(event) => setView(event.target.value as 'summary' | 'detail')}
            >
              <option value="summary">Summary per customer</option>
              <option value="detail">Detail per statement</option>
            </select>
          </Field>
        </div>
      </Card>

      {report.isLoading && <Skeleton className="h-64 w-full" />}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {BUCKET_ORDER.map((bucket) => (
              <Card key={bucket}>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  {data.bucketLabels[bucket]}
                </p>
                <p
                  className={`tabular text-lg font-bold ${
                    bucket === 'D120_PLUS' && Number(data.bucketTotals[bucket]) > 0
                      ? 'text-red-600'
                      : ''
                  }`}
                >
                  {peso(data.bucketTotals[bucket])}
                </p>
              </Card>
            ))}
            <Card className="bg-navy text-white dark:bg-navy">
              <p className="text-xs uppercase tracking-wide text-white/70">Total outstanding</p>
              <p className="tabular text-lg font-bold">{peso(data.grandTotal)}</p>
            </Card>
          </div>

          <Card
            title={view === 'summary' ? 'Per customer' : 'Per statement'}
            description={`Generated by ${data.generatedBy} · as of ${shortDate(data.asOf)}`}
          >
            {data.detail.length === 0 ? (
              <EmptyState title="Nothing outstanding" description="Every statement is settled." />
            ) : view === 'summary' ? (
              <TableWrap>
                <thead>
                  <tr>
                    <Th>Customer</Th>
                    {BUCKET_ORDER.map((bucket) => (
                      <Th key={bucket} align="right">
                        {data.bucketLabels[bucket]}
                      </Th>
                    ))}
                    <Th align="right">Total</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data.summary.map((row) => (
                    <tr key={row.customerId}>
                      <Td>
                        <Link to={`/customers/${row.customerId}`} className="hover:underline">
                          {row.customerName}
                        </Link>
                      </Td>
                      {BUCKET_ORDER.map((bucket) => (
                        <Td key={bucket} align="right" className="tabular">
                          {Number(row.buckets[bucket]) === 0 ? '—' : peso(row.buckets[bucket])}
                        </Td>
                      ))}
                      <Td align="right" className="tabular font-semibold">
                        {peso(row.total)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-bold dark:border-slate-700">
                    <Td>All customers</Td>
                    {BUCKET_ORDER.map((bucket) => (
                      <Td key={bucket} align="right" className="tabular">
                        {peso(data.bucketTotals[bucket])}
                      </Td>
                    ))}
                    <Td align="right" className="tabular">
                      {peso(data.grandTotal)}
                    </Td>
                  </tr>
                </tfoot>
              </TableWrap>
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <Th>Customer</Th>
                    <Th>Statement</Th>
                    <Th>Billed</Th>
                    <Th>Due</Th>
                    <Th align="right">Days</Th>
                    <Th>Bucket</Th>
                    <Th align="right">Original</Th>
                    <Th align="right">Settled</Th>
                    <Th align="right">Outstanding</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data.detail.map((row) => (
                    <tr key={row.billingId}>
                      <Td>{row.customerName}</Td>
                      <Td>
                        <Link
                          to={`/billing/${row.billingId}`}
                          className="font-mono text-xs hover:underline"
                        >
                          {row.code}
                        </Link>
                        <p className="text-xs text-slate-500">{row.salesOrderCode}</p>
                      </Td>
                      <Td>{shortDate(row.billingDate)}</Td>
                      <Td>
                        <span className={row.overdue ? 'font-semibold text-red-600' : ''}>
                          {shortDate(row.dueDate)}
                        </span>
                      </Td>
                      <Td align="right" className="tabular">
                        {row.days}
                      </Td>
                      <Td>
                        <StatusChip
                          tone={
                            row.bucket === 'CURRENT'
                              ? 'ok'
                              : row.bucket === 'D120_PLUS'
                                ? 'critical'
                                : 'warning'
                          }
                        >
                          {data.bucketLabels[row.bucket]}
                        </StatusChip>
                      </Td>
                      <Td align="right" className="tabular">
                        {peso(row.total)}
                      </Td>
                      <Td align="right" className="tabular">
                        {peso(row.settled)}
                      </Td>
                      <Td align="right" className="tabular font-semibold">
                        {peso(row.outstanding)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
